
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { ApiService } from '../../../core/services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { SharedService } from '../../../shared/services/shared.service';
import { Utils } from '../../../Utils/utils';
import { FieldValue, ServiceName } from '../../../shared/constants/services.enum';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { AccountModel } from '../../../shared/models/loginuser.model';
import { ApplicationRequest } from '../../../shared/models/application-request.model';
import { LovSelectionService } from '../../../core/services/lov-selection.service';
import { OptionTranslateService } from '../../../shared/services/option-translate.service';
import { SrStatus } from '../../../shared/constants/status.enum';
import * as XLSX from 'xlsx';
import { DcMethodValues } from '../../../shared/constants/dc-method.constants';

@Component({
  selector: 'app-applications',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.scss'
})
export class ApplicationsComponent implements OnChanges, OnInit {
  @Input() pageTitle: string = "";
  @Input() serviceType: any = null;

  requestPayload: ApplicationRequest = new ApplicationRequest();
  lookupKey = LookupKey;
  serviceName = ServiceName;

  loader: boolean = false;
  pageSize: any = 10;
  readonly exportPageSize: number = 3000;
  totalRecords: number = 0;
  pageIndex: number = 1;

  filterForm: FormGroup;
  applications: any = [];
  users: any = [];
  dateRanges: any = []

  accountCSN: string = "";
  accountId: string = ""
  lang: string = "en";
  initialLoad: boolean = true;
  userType: string = "";
  loginUserName: string = ""

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private api: ApiService,
    private sharedService: SharedService,
    private lovService: LovSelectionService,
    private optionTranslateService: OptionTranslateService,
  ) {
    this.loginUserName = this.sharedService.getUserProfile()?.['Login Name'] || '';
    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];
    this.lang = this.sharedService.getCurrentLang();
    this.dateRanges = this.optionTranslateService.getOptions(
      'applicationDays',
      this.lang
    );
    this.filterForm = this.fb.group({
      service: [this.serviceType],
      subService: [null],
      membership: [null],
      status: [null],
      user: [null],
      days: ["15"],
      search: ''
    });
    this.accountCSN = this.sharedService.getCsnAccountId('csn'); //1298
    this.accountId = this.sharedService.getCsnAccountId('id'); //7093
    let accounts = Utils.checkIsArray(this.sharedService.getUserProfile()?.Account) ?? [];
    accounts?.map((u: AccountModel) => {
      if (u['DC Name English']) {
        let obj = {
          Name: u['CSN']
            ? `${u['DC Name English']} (${u['CSN']})`
            : `${u['DC Name English']}`,
          Value: u['CSN'] ? u['CSN'] : u['Id'],
          isCSN: !!u['CSN']
        };
        this.users.push(obj);
      }
    })
    this.filterForm.get("membership")?.setValue(this.accountCSN ? this.accountCSN : this.accountId);

    //Read action form query for AwaitingClarification
    this.route.queryParams.subscribe(params => {
      const qAction = (params['action'] || '').toString().trim();
      if (!qAction) {
        return;
      }
      if (qAction === 'ac') {
        this.filterForm.get("status")?.setValue(SrStatus.AwaitingClarification);
        this.filterForm.get("days")?.setValue(365);
        return;
      }
      this.filterForm.get("status")?.setValue(qAction);
      this.filterForm.get("days")?.setValue(365);
    });

    this.getLinkedUsers();
  }

  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    if (sessionStorage.getItem("srNumber")) {
      sessionStorage.removeItem("srNumber")
    }
  }
  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    this.filterForm.get("service")?.setValue(this.serviceType);
    if (this.serviceType == ServiceName.ATA_CARNET) {
      this.filterForm.get("service")?.disable();
    }
    if (this.serviceType == ServiceName.MEDIATION_SERVICES || this.serviceType == ServiceName.ATA_CARNET) {
      this.getApplications();
    }
  }
  // This method is used to search
  search() {
    this.pageIndex = 1;
    this.getApplications();
  }
  // This method is used to remove search using event
  removeSearch(event: KeyboardEvent) {
    const value = this.filterForm.get('search')?.value?.trim() || '';
    if (value === '') {
      this.search();
    }
  }

  // This method is used to handle service change event using option
  onServiceChange(option: any) {
    this.lovService.setLovSelection(option.lovType, option.selectedLov);
    this.filterForm.get("subService")?.setValue(null);
    if (!option.selectedLov) {
      this.getApplications();
    }
  }

  // This method is used to apply filter using option
  applyFilter(option: any) {
    if (this.initialLoad) {
      return;
    }
    this.pageIndex = 1;
    this.getApplications();
  }
  // This method is used to filter by membership
  filterByMembership(option: any) {
    this.accountCSN = "";
    if (option && option?.selectedLov?.isCSN) {
      this.accountCSN = option.selectedLov && option.selectedLov?.Value ? option.selectedLov.Value : this.sharedService.getCsnAccountId('csn');
    } else {
      this.accountId = option?.selectedLov && option.selectedLov?.Value ? option.selectedLov.Value : this.sharedService.getCsnAccountId('id');
    }
    this.pageIndex = 1;
    this.getLinkedUsers();
    this.getApplications();
  }

  membershipSelectedUser: string = "";
  filterByLinkedUser(option: any) {
    if (option && option?.selectedLov) {
      this.membershipSelectedUser = option.selectedLov?.['Login Name'];
    }
    this.pageIndex = 1;
    this.getApplications();
  }

  private buildRequestPayload(pageSize: number, startRowNum: number, newQuery: boolean): ApplicationRequest {
    this.requestPayload = new ApplicationRequest();
    const { body } = this.requestPayload;
    const serviceRequest = body.IncomingHierarchy.ListOfDCGetSRSummaryIO['Service Request'];
    let primaryCSN = "";
    if (this.filterForm.get('search')?.value && !this.accountCSN) {
      primaryCSN = this.sharedService.getCsnAccountId('csn');
    }
    body.Language = this.lang === 'ar' ? 'ARA' : 'ENU';
    body.IncomingHierarchy.MessageId = Utils.generateUniqueId();

    serviceRequest['Account Id'] = this.accountCSN ? '' : this.accountId;
    serviceRequest.CSN = primaryCSN || this.accountCSN;
    serviceRequest['SR Number'] = this.filterForm.get('search')?.value?.trim() || '';
    serviceRequest['SR Type'] = this.filterForm.get('service')?.value || '';
    serviceRequest['SR Sub Type'] = this.filterForm.get('subService')?.value || '';
    serviceRequest['Status'] = this.filterForm.get('status')?.value || '';
    serviceRequest['Created By Name'] = this.membershipSelectedUser ? this.membershipSelectedUser : "";

    body.PageSize = pageSize;
    body.StartRowNum = startRowNum;
    body.NewQuery = newQuery;
    body.NoDays = this.filterForm.get('days')?.value || 30;

    return this.requestPayload;
  }

  private hasValidSrResponse(response: any): boolean {
    const hasNoErrorCode = response?.['Error Code'] == null || response?.['Error Code'] === '';
    const hasNoErrorMessage = response?.['Error Message'] == null || response?.['Error Message'] === '';
    const hasServiceRequests = !!response?.SiebelMessage?.['Service Request'];
    return hasNoErrorCode && hasNoErrorMessage && hasServiceRequests;
  }
  // get list of SR/Application
  // This method is used to get applications
  getApplications() {
    this.loader = true;
    this.selectedRows = [];
    this.totalRecords = 0;
    this.applications = [];
    const payload = this.buildRequestPayload(
      this.pageSize,
      this.pageSize * (this.pageIndex - 1),
      this.pageIndex === 1
    );

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SR_SUMMARY, payload)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          this.loader = false;
          if (!this.hasValidSrResponse(response)) {
            return;
          }
          this.totalRecords = response.TotalRecords;
          const applicationRows = Utils.checkIsArray(response?.SiebelMessage['Service Request']);
          this.applications = applicationRows.map((app: any) => ({
            ...app,
            'Display Member Name': this.getApplicationMemberDisplayName(app),
          }));
          if (this.initialLoad) {
            this.initialLoad = false;
            return; // skip first time
          }
        },
        // This method is used to handle the error callback
        error: () => {
          this.loader = false;
        }
      })
  }

  // This method is used to page change using page
  pageChange(page: any) {
    this.pageIndex = page;
    this.selectedRows = []
    this.getApplications();
  }
  selectedRows: any = [];
  showPayAction: boolean = false;

  // This method is used to select row using rows
  selectRow(rows: any) {
    this.selectedRows = rows;
    this.showPayAction = this.selectedRows.some(
      (row: any) => row.Status === SrStatus.ApprovedAndPaymentAwaited || row.Status === SrStatus.AwaitingFinalPayment
    );
  }

  // This method is used to pay for mulitple
  payForMulitple() {
    // Filter only rows with the required status
    const payableRows = this.selectedRows.filter(
      (row: any) => (row.Status === SrStatus.ApprovedAndPaymentAwaited || row.Status === SrStatus.AwaitingFinalPayment) && row['DC Online Pay Flag'] != 'Y'
    );
    //SR numbers
    const srNumbers = payableRows.map((row: any) => row["SR Number"]);

    if (srNumbers && srNumbers.length > 0) {
      // Join as comma-separated for query param
      const srList = srNumbers.join(",");
      // Navigate with query parameter
      this.router.navigate(
        [`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}`],
        { queryParams: { payfor: srList } }
      );
    }
  }

  // This method is used to apply for service
  applyForService() {
    this.router.navigate([RoutesPath.SERVICES_LIST]);
  }
  // This method is used to export csv
  exportCSV() {
    this.loader = true;
    const payload = this.buildRequestPayload(this.exportPageSize, 0, true);

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SR_SUMMARY, payload)
      .subscribe({
        next: (response: any) => {
          this.loader = false;
          if (!this.hasValidSrResponse(response)) {
            return;
          }

          const exportData = Utils.checkIsArray(response?.SiebelMessage['Service Request']);
          if (!exportData.length) {
            return;
          }

          const worksheetData = [
            ['Application', 'Service', 'SR Sub Type', 'Membership/ User', 'Invoice Number', 'Date', 'Status'],
            ...exportData.map((app: any) => ([
              app['SR Number'] ?? '',
              app['SR Type'] ?? '',
              app['SR Sub Type'] ?? app['Sub Type'] ?? '',
              this.getApplicationMemberDisplayName(app),
              app['DC Invoice Number'] ?? '',
              app['DC Created'] ?? '',
              app['Status'] ?? ''
            ]))
          ];

          const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

          const fileName = `applications_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(workbook, fileName);
        },
        error: () => {
          this.loader = false;
        }
      });
  }

  // This method is used to view app using sr
  viewApp(sr: any) {
    this.sharedService.gotoDetailPage(sr);
  }

  // This method is used to goto payment page using application
  gotoPaymentPage(application: any) {
    const csn = String(application?.['CSN']).trim();
    
    if(application['DC Online Pay Flag'] === 'Y') {
      this.sharedService.toastMessage('INFO', 'ONLINE_PAYMENT_NOTALLOWED', 'info', true); 
      return;
    }

    this.router.navigate(
      [`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${application["SR Number"]}`],
      { queryParams: csn ? { csn } : {} }
    );
  }

  // This method us used to download multiple certificates
  downloadCertificates() {
    const allowedStatuses = [
      SrStatus.Paid,
      SrStatus.Closed,
      SrStatus.AwaitingCarnetReturn
    ];
    const eligibleRows = Utils.checkIsArray(this.selectedRows).filter(
      (row: any) =>
        allowedStatuses.includes(row.Status) &&
        Utils.checkIsValidURL(row["DCDownloadURL"])
    );

    if (!eligibleRows.length) {
      this.sharedService.toastMessage(
        'INFO',
        'DOWNLOAD_CERTIFICATE_STATUS_MESSAGE',
        'info',
        true
      );
      return;
    }

    eligibleRows.forEach((row: any) => {
      window.open(row["DCDownloadURL"], '_blank', 'noopener');
    });
  }

  userLoader = false;
  linkedUsers: any = [];
  getLinkedUsers() {
    if (!this.accountCSN) {
      this.linkedUsers = [];
      return;
    }
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Account Contacts WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.APPLICATIONS,
        "CSN": this.accountCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          this.userLoader = false;
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            if (response.SiebelMessage?.["Account"] && response.SiebelMessage?.["Account"]?.["Contact"]) {
              this.linkedUsers = Utils.checkIsArray(response.SiebelMessage?.["Account"]?.["Contact"]);
            }
          }
        },
        // This method is used to handle the error callback using err
        error: (err: any) => {
          this.userLoader = false;
        }
      });
  }

  //This method is use to download receipt
  downloadReceipts() {
    const receipts = Utils.checkIsArray(this.selectedRows)
      .map((row: any) => row?.['DC eReceipt Download URL'])
      .filter((url: unknown): url is string => Utils.checkIsValidURL(url));

    if (!receipts.length) {
      this.sharedService.toastMessage(
        'INFO',
        'DOWNLOAD_RECEIPT_STATUS_MESSAGE',
        'info',
        true
      );
      return;
    }

    receipts.forEach((url, index) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = Utils.extractFileNameFromUrl(url) || `receipt-${index + 1}`;
      link.rel = 'noopener';
      link.target = '_blank';
      link.click();
      link.remove();
    });
  }

  private getApplicationMemberDisplayName(app: any): string {
    const isCertificateOfOrigin = app?.['SR Type'] == ServiceName.COO;

    if (isCertificateOfOrigin && app?.['DC Exporter Name (Eng)']) {
      if (this.lang === 'ar') {
        return app?.['DC Exporter Name (Ara)'] || app?.['DC Exporter Name (Eng)'];
      }
      return app?.['DC Exporter Name (Eng)'] || app?.['DC Exporter Name (Ara)'];
    }

    if (this.lang === 'ar') {
      return app?.['DC Member Name (Arabic)'] || app?.['DC Member Name (English)'] || '';
    }
    return app?.['DC Member Name (English)'] || app?.['DC Member Name (Arabic)'] || '';
  }
}
